import { useEffect } from 'react';
import { Box } from '@mui/material';
import YourNotesRow from '../../YourNotesTab/YourNotesRow';
import searchEngine from '../Core/SearchEngine';
import searchQueryEngine from '../Core/SearchQueryEngine';
import useSearchQuery from '../useSearchQuery';
import useSearchResults from '../useSearchResults';
import { NAV_KEYS } from '../../../../hooks/navKeys';

/**
 * Renders the search result list.
 * Calls its own hooks directly — no Context, no prop drilling.
 * Focus-on-Enter (from SearchQueryInput) targets this element via #ys-result-list,
 * same DOM-lookup style already used here for the search input — no ref needed.
 * Keyboard nav: nav keys (NAV_KEYS) handled by useKeyboardNav in parent, left alone here.
 * Any other printable key refocuses the search input. Escape clears query and refocuses input.
 */
export default function SearchResultList() {
    const query = useSearchQuery();
    const { results, total, page, pageSize, selectedEntry, loading } = useSearchResults();
    const selectedIndex = results.findIndex((e) => e.rk === selectedEntry?.rk);

    // Scroll selected entry into view when selection changes
    useEffect(() => {
        if (!selectedEntry) return;
        setTimeout(() => {
            const selected = document.querySelector('#ys-result-list .fr-card--selected');
            selected?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }, 50);
    }, [selectedEntry, results]);

    const handleKeyDown = (e) => {
        if (NAV_KEYS.includes(e.key)) return;
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        if (e.key === 'Escape') {
            searchQueryEngine.clearQuery();
            document.querySelector('.ys-search-input')?.focus();
            return;
        }
        if (e.key.length === 1) document.querySelector('.ys-search-input')?.focus();
    };

    return (
        <Box
            id="ys-result-list"
            className="ys-list"
            tabIndex={0}
            onKeyDown={handleKeyDown}
        >
            {results.length === 0 && !loading && (
                <Box className="ys-empty">
                    {query.length < 3 ? 'Type at least 3 characters to search' : 'No entries found'}
                </Box>
            )}
            {results.map((e) => (
                <YourNotesRow
                    key={e.rk}
                    entry={e}
                    selected={selectedEntry?.rk === e.rk}
                    onClick={(entry) => searchEngine.selectEntry(entry)}
                    position={selectedEntry?.rk === e.rk ? (page - 1) * pageSize + selectedIndex + 1 : null}
                    total={total}
                />
            ))}
        </Box>
    );
}
